// @flow
import type {ComponentType, Node} from 'react'
import React, {useCallback, useMemo} from 'react'
import {graphql, useFragment} from 'react-relay'

import DisabledForRestrictedPlanButton from 'components/ui/DisabledForRestrictedPlanButton'
import DisabledButtonWithTooltip from 'components/ui/DisabledButtonWithTooltip'
import ProposeOutsideMeetingMutation from 'mutations/ProposeOutsideMeetingMutation'
import ProposeNowLoader from './ProposeNowLoader'
import type {
  ProposeNow_proposal$key as Proposal,
} from './__generated__/ProposeNow_proposal.graphql'

type Props = {
  idPrefix: string,
  proposal: Proposal,
  buttonTag: 'button' | ComponentType<*>,
  onSuccess: () => void,
  onBeforePropose: () => void,
  className: string,
  ...
}

const proposalFragment = graphql`
  fragment ProposeNow_proposal on Proposal {
    id
    status
    databaseId
    canBeAccepted
    hasChanges
    expectedResponseCount
    
    organization {
      asyncProposalsAreEnabled
    }
    
    circle {
      isGovernanceEnabled
    }
  }
`

function ProposeNow({
  proposal: proposalKey,
  buttonTag,
  className,
  onBeforePropose,
  onSuccess,
  idPrefix,
  ...buttonProps
}: Props): null | Node {
  const proposal = useFragment(proposalFragment, proposalKey)

  const willAutoAccept = proposal.expectedResponseCount === 0

  const label = willAutoAccept
    ? I18n.t('proposals.react.accept_now')
    : I18n.t('proposals.react.propose_now')

  const tooltipLabel = useMemo(() => {
    if (proposal.status === 'DRAFT_WITH_CONFLICTS')
      return I18n.t('tensions.react.propose_now_tooltip_for_conflicts')

    if (!proposal.hasChanges)
      return I18n.t('proposals.react.empty_proposal')

    if (!proposal.canBeAccepted)
      return I18n.t('proposals.react.waiting_for_changes')

    if (proposal.circle && !proposal.circle.isGovernanceEnabled)
      return I18n.t('proposal.proposals_disabled_by_governance')

    return null
  }, [proposal])

  const tooltipProps = useMemo(() => ({
    ...buttonProps,
    id: `${idPrefix}-propose-now-${proposal.databaseId}`,
    placement: 'left',
    title: label,
    buttonTag,
    className,
  }), [idPrefix, proposal, label, buttonProps, buttonTag, className])

  const proposeOutsideMeeting = useCallback(() => {
    onBeforePropose()
    ProposeOutsideMeetingMutation({proposalId: proposal.id})
      .then(onSuccess)
  }, [proposal, onBeforePropose, onSuccess])

  const onClick = useCallback(() => {
    const confirmationMessage = willAutoAccept
      ? I18n.t('proposals.react.accept_proposal_now')
      : I18n.t('proposals.react.send_proposal_now')

    // eslint-disable-next-line no-alert
    if (window.confirm(confirmationMessage))
      proposeOutsideMeeting()
  }, [willAutoAccept, proposeOutsideMeeting])

  const renderBody = useCallback(() => {
    if (tooltipLabel)
      return (<DisabledButtonWithTooltip {...tooltipProps} tooltip={tooltipLabel} />)

    const Button = buttonTag

    return (
      <Button {...buttonProps} className={className} onClick={onClick}>
        {label}
      </Button>
    )
  }, [tooltipLabel, buttonTag, tooltipProps, buttonProps, onClick, label, className])

  if (!proposal.organization.asyncProposalsAreEnabled && !willAutoAccept)
    return null

  return (
    <DisabledForRestrictedPlanButton {...tooltipProps}>
      {renderBody()}
    </DisabledForRestrictedPlanButton>
  )
}

ProposeNow.defaultProps = {
  onBeforePropose: () => {},
  onSuccess: () => {},
  className: '',
}

export default ProposeNow

export {
  ProposeNowLoader,
}
